import React, { useEffect, useState } from 'react';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import TextField from '@material-ui/core/TextField';
import { toast } from '@jumbo/components/custom/Toast';
import GridContainer from '../../../@jumbo/components/GridContainer';
import PageContainerDashboard from '../../../@jumbo/components/PageComponents/layouts/PageContainerDasboard';
import { getUsersLogAll, getUsersLogByEntidad } from 'services/api';

const breadcrumbs = [
  { label: 'Inicio', link: '/' },
  { label: 'Log de usuarios', isActive: true },
];

const columns = [
  { id: 'usuario', label: 'Usuario' },
  { id: 'entidad', label: 'Entidad' },
  { id: 'fecha', label: 'Fecha' },
  { id: 'hora', label: 'Hora' },
];

const formatFecha = fechaStr => {
  if (!fechaStr) return '-';
  const d = new Date(fechaStr);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

const formatHora = fechaStr => {
  if (!fechaStr) return '-';
  const d = new Date(fechaStr);
  return d.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const LogTable = ({ rows, loading }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
        <CircularProgress />
      </div>
    );
  }

  if (!rows.length) {
    return <div style={{ padding: 16, color: '#888' }}>No hay datos para mostrar.</div>;
  }

  const displayed = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <>
      <TableContainer component={Paper} elevation={0} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow style={{ backgroundColor: '#f0f4f8' }}>
              {columns.map(col => (
                <TableCell key={col.id} style={{ fontWeight: 'bold', color: '#2d5583' }}>
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {displayed.map((row, index) => (
              <TableRow key={row.id ?? index} hover>
                <TableCell>{row.usuario}</TableCell>
                <TableCell>{row.entidad}</TableCell>
                <TableCell>{formatFecha(row.fecha_hora)}</TableCell>
                <TableCell>{formatHora(row.fecha_hora)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={rows.length}
        page={page}
        onChangePage={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onChangeRowsPerPage={e => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50, 100]}
        labelRowsPerPage="Filas por página:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
      />
    </>
  );
};

const tabBarStyle = {
  display: 'flex',
  borderBottom: '1px solid #cdcdcd',
};

const getTabStyle = isSelected => ({
  padding: '10px 20px',
  cursor: 'pointer',
  fontWeight: 'bold',
  color: isSelected ? '#2d5583' : '#888888',
  borderBottom: isSelected ? '3px solid #2d5583' : '3px solid transparent',
  marginBottom: -1,
  userSelect: 'none',
});

const workAreaStyle = {
  borderStyle: 'solid',
  borderColor: '#cdcdcd',
  borderWidth: '0 1px 1px 1px',
  borderRadius: '0 0 5px 5px',
  padding: '16px',
};

const UsersLog = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [allLogs, setAllLogs] = useState([]);
  const [loadingAll, setLoadingAll] = useState(false);
  const [entidadInput, setEntidadInput] = useState('');
  const [entidadLogs, setEntidadLogs] = useState([]);
  const [loadingEntidad, setLoadingEntidad] = useState(false);
  const [hasBuscado, setHasBuscado] = useState(false);

  useEffect(() => {
    setLoadingAll(true);
    getUsersLogAll()
      .then(res => setAllLogs(res.data))
      .catch(() => toast.error('Error al cargar el log de usuarios'))
      .finally(() => setLoadingAll(false));
  }, []);

  const handleBuscar = () => {
    if (!entidadInput.trim()) return;
    setLoadingEntidad(true);
    setHasBuscado(true);
    getUsersLogByEntidad(entidadInput.trim())
      .then(res => setEntidadLogs(res.data))
      .catch(() => toast.error('Error al buscar registros de la entidad'))
      .finally(() => setLoadingEntidad(false));
  };

  return (
    <PageContainerDashboard heading="Log de Usuarios" breadcrumbs={breadcrumbs}>
      <GridContainer>
        <Grid item xs={12}>
          <div style={tabBarStyle}>
            <div style={getTabStyle(selectedTab === 0)} onClick={() => setSelectedTab(0)}>
              Todos los usuarios
            </div>
            <div style={getTabStyle(selectedTab === 1)} onClick={() => setSelectedTab(1)}>
              Por entidad
            </div>
          </div>

          <div style={workAreaStyle}>
            {selectedTab === 0 && <LogTable rows={allLogs} loading={loadingAll} />}

            {selectedTab === 1 && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                  <TextField
                    label="Nombre de entidad"
                    variant="outlined"
                    size="small"
                    value={entidadInput}
                    onChange={e => setEntidadInput(e.target.value)}
                    onKeyPress={e => {
                      if (e.key === 'Enter') handleBuscar();
                    }}
                    style={{ minWidth: 320 }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleBuscar}
                    disabled={!entidadInput.trim() || loadingEntidad}>
                    Buscar
                  </Button>
                </div>
                {hasBuscado && <LogTable rows={entidadLogs} loading={loadingEntidad} />}
              </div>
            )}
          </div>
        </Grid>
      </GridContainer>
    </PageContainerDashboard>
  );
};

export default UsersLog;
